"use client";

import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { Sidebar } from "@/components/layout/sidebar";
import { Header } from "@/components/layout/header";
import { AppErrorBoundary } from "@/components/layout/error-boundary";
import { logger } from "@/lib/telemetry/logger";
import { cn } from "@/lib/utils";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const router = useRouter();
  const { user, loading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const hasLoggedMountRef = useRef(false);

  useEffect(() => {
    // Only log mount once when loading completes and user is available
    if (!loading && !hasLoggedMountRef.current) {
      logger.debug("Dashboard layout mounted", {
        authenticated: !!user,
        loading,
      });
      hasLoggedMountRef.current = true;
    }

    return () => {
      // Only log unmount in development to reduce noise
      if (process.env.NODE_ENV === "development") {
        logger.debug("Dashboard layout unmounted");
      }
      hasLoggedMountRef.current = false;
    };
  }, [loading, user]); // Track loading and user, but only log once

  useEffect(() => {
    try {
      const stored = localStorage.getItem("ref.sidebarCollapsed");
      if (stored === "true" || stored === "false") {
        setSidebarCollapsed(stored === "true");
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("ref.sidebarCollapsed", String(sidebarCollapsed));
    } catch {
      // ignore
    }
  }, [sidebarCollapsed]);

  // Redirect to login if not authenticated
  // Middleware should handle this, but this is a safety check
  // Don't redirect on setup-profile or investor-profile pages as they might be accessed during initial setup
  useEffect(() => {
    if (!loading && !user) {
      // Use usePathname hook to get current path safely
      if (typeof window !== "undefined") {
        const currentPath = window.location.pathname;
        // Don't redirect if we're on setup-profile or investor-profile (user might be setting up)
        if (
          !currentPath.startsWith("/setup-profile") &&
          !currentPath.startsWith("/investor-profile")
        ) {
          logger.debug(
            "Dashboard layout - not authenticated, redirecting to login",
          );
          router.push("/login");
        }
      } else {
        // Server-side or initial render - let middleware handle it
        logger.debug(
          "Dashboard layout - not authenticated, redirecting to login",
        );
        router.push("/login");
      }
    }
  }, [loading, user, router]);

  // Show loading while checking session
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Render dashboard when authenticated
  // Optimized to reduce forced reflow:
  // - Use CSS containment to isolate layout changes
  // - Direct margin calculation (more efficient than CSS variables)
  // - Smooth transition with hardware acceleration hints
  return (
    <div className="flex min-h-screen">
      <Sidebar
        collapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <div
        className={cn(
          "flex flex-1 flex-col transition-[margin-left] duration-300 ease-in-out ml-0",
          sidebarCollapsed ? "lg:ml-20" : "lg:ml-64",
        )}
        style={
          {
            // CSS containment isolates layout changes to this element
            // This prevents forced reflow from propagating to parent/children
            contain: "layout style",
          } as React.CSSProperties
        }
      >
        <Header onMobileMenuToggle={() => setMobileMenuOpen(true)} />
        <main className="flex-1 p-4 lg:p-6">
          <AppErrorBoundary>{children}</AppErrorBoundary>
        </main>
      </div>
    </div>
  );
}
